import React, {useEffect, useState} from 'react';
import axios from "axios";
import {toast} from 'react-toastify';
// @ts-ignore
import {PaperPlaneTilt, Plus, Trash, MagnifyingGlass, X} from '@phosphor-icons/react';
import {getBaseUrlNodeServer} from "../../config";

const STATUS_FILTERS = ['All', 'Draft', 'Live', 'Paused', 'Done'];

function statusBadgeClasses(status) {
	const s = (status || 'Draft').toString();
	switch (s) {
		case 'Live':
		case 'Running':
			return 'bg-emerald-100 text-emerald-800';
		case 'Paused':
			return 'bg-amber-100 text-amber-800';
		case 'Done':
		case 'Completed':
			return 'bg-slate-200 text-slate-800';
		case 'Ready':
			return 'bg-blue-100 text-blue-800';
		default:
			return 'bg-slate-100 text-slate-700';
	}
}

function displayStatus(status) {
	if (status === undefined || status === null || status === '') return 'Draft';
	return status.toString();
}

function formatRate(numerator, denominator) {
	const denom = Number(denominator);
	if (!denom || denom <= 0) return '—';
	const num = Number(numerator) || 0;
	const pct = (num / denom) * 100;
	if (!isFinite(pct)) return '—';
	if (pct > 0 && pct < 0.1) return '<0.1%';
	return `${pct.toFixed(1)}%`;
}

function formatNumber(n) {
	if (n === null || n === undefined || n === '') return '—';
	const v = Number(n);
	if (!isFinite(v)) return '—';
	return v.toLocaleString();
}

function formatLastSent(campaign) {
	const v = campaign.LastSent || campaign.lastSent || campaign.lastsent || campaign.SentAt || campaign.sentAt;
	if (!v) return '—';
	const d = new Date(v);
	if (isNaN(d.getTime())) return '—';
	return d.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
}

const TableSkeleton = () => (
	<div className="rounded-2xl border border-slate-100 shadow-sm overflow-hidden bg-white animate-pulse">
		<div className="bg-slate-50 h-11 border-b border-slate-100"/>
		{Array.from({length: 5}).map((_, i) => (
			<div key={i} className="flex items-center gap-4 px-4 py-4 border-b border-slate-100 last:border-b-0">
				<div className="h-4 w-40 bg-slate-100 rounded"/>
				<div className="h-5 w-16 bg-slate-100 rounded-full"/>
				<div className="h-4 w-12 bg-slate-100 rounded ml-auto"/>
				<div className="h-4 w-12 bg-slate-100 rounded"/>
				<div className="h-4 w-12 bg-slate-100 rounded"/>
				<div className="h-4 w-20 bg-slate-100 rounded"/>
				<div className="h-7 w-7 bg-slate-100 rounded-full"/>
			</div>
		))}
	</div>
);

const EmptyState = ({onCreate}) => (
	<div className="rounded-2xl border border-slate-100 bg-white shadow-sm py-16 px-6 text-center">
		<div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
			<PaperPlaneTilt size={32} weight="duotone" color="#10B981"/>
		</div>
		<h3 className="text-xl font-semibold text-slate-900 mb-2">No campaigns yet</h3>
		<p className="text-base text-slate-600 mb-6 max-w-md mx-auto">
			Your campaigns will land here. Spin up your first one in seconds and start reaching the inbox.
		</p>
		<button
			type="button"
			onClick={onCreate}
			className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-sm transition-colors">
			<Plus size={18} weight="bold"/>
			Create your first campaign
		</button>
	</div>
);

const NewCampaignModal = ({open, onClose, onSubmit, submitting}) => {
	const [name, setName] = useState('');

	useEffect(() => {
		if (!open) setName('');
	}, [open]);

	if (!open) return null;

	const handleSubmit = (e) => {
		e.preventDefault();
		const trimmed = name.trim();
		if (!trimmed) return;
		onSubmit(trimmed);
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4"
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}>
			<div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
				<div className="flex items-start justify-between px-6 pt-6 pb-2">
					<div>
						<h2 className="text-xl font-semibold text-slate-900">New campaign</h2>
						<p className="text-sm text-slate-600 mt-1">
							Give it a name. You can configure leads, template, and schedule on the next screen.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="text-slate-500 hover:text-slate-900 -mr-1 -mt-1 p-1 rounded-full hover:bg-slate-100 transition"
						aria-label="Close">
						<X size={18}/>
					</button>
				</div>
				<form onSubmit={handleSubmit} className="px-6 pb-6 pt-4">
					<label htmlFor="campaignname" className="block text-sm font-medium text-slate-700 mb-1.5">
						Campaign name
					</label>
					<input
						id="campaignname"
						type="text"
						value={name}
						onChange={(e) => setName(e.target.value)}
						required
						autoFocus
						placeholder="Spring product launch"
						className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition"/>
					<div className="flex justify-end gap-3 mt-6">
						<button
							type="button"
							onClick={onClose}
							className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-6 py-2.5 font-medium transition">
							Cancel
						</button>
						<button
							type="submit"
							disabled={submitting || !name.trim()}
							className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 disabled:cursor-not-allowed text-white rounded-full px-6 py-2.5 font-semibold shadow-sm transition-colors">
							{submitting ? 'Creating…' : 'Create campaign'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

const NewCampaign = ({onChildAction, setcurrentCampaign}) => {
	const verifiedemail = sessionStorage.getItem("verifiedemail");
	const [isLoading, setIsLoading] = useState(true);
	const [usercampaigns, setusercampaigns] = useState([]);
	const [filter, setFilter] = useState('All');
	const [search, setSearch] = useState('');
	const [showModal, setShowModal] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const fetchCampaigns = () => {
		axios.defaults.baseURL = getBaseUrlNodeServer();
		return axios.post('getcampaigns', {'verifiedemail': verifiedemail})
			.then(response => {
				setusercampaigns(Array.isArray(response.data) ? response.data : []);
			})
			.catch(err => console.log(err));
	};

	useEffect(() => {
		(async () => {
			try {
				await fetchCampaigns();
			} catch (error) {
				console.error('Error during fetch:', error.message);
			} finally {
				setIsLoading(false);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleCreate = async (name) => {
		try {
			setSubmitting(true);
			axios.defaults.baseURL = getBaseUrlNodeServer();
			await axios.post('addcampaign', {'verifiedemail': verifiedemail, 'campaignname': name});
			toast.success('Campaign created');
			await fetchCampaigns();
			setShowModal(false);
			// Drop the user straight into the edit surface for the new campaign.
			setcurrentCampaign({campaignname: name});
			onChildAction(1);
		} catch (err) {
			console.log(err);
			toast.error('Failed to create campaign');
		} finally {
			setSubmitting(false);
		}
	};

	const deleteCampaign = (e, campaign) => {
		e.stopPropagation();
		// eslint-disable-next-line no-alert
		if (!window.confirm(`Delete "${campaign.campaignname}"? This can't be undone.`)) return;
		axios.defaults.baseURL = getBaseUrlNodeServer();
		axios.post('deletecampaign', {'verifiedemail': verifiedemail, 'campaignname': campaign.campaignname})
			.then(() => {
				toast.success('Campaign deleted');
				fetchCampaigns();
			})
			.catch(err => {
				console.log(err);
				toast.error('Failed to delete campaign');
			});
	};

	const clickedCampaign = (campaign) => {
		setcurrentCampaign(campaign);
		onChildAction(1);
	};

	const filtered = usercampaigns.filter((c) => {
		const status = displayStatus(c.Status);
		if (filter !== 'All' && status !== filter) return false;
		if (search && !(c.campaignname || '').toLowerCase().includes(search.toLowerCase())) return false;
		return true;
	});

	const counts = STATUS_FILTERS.reduce((acc, s) => {
		acc[s] = s === 'All'
			? usercampaigns.length
			: usercampaigns.filter(c => displayStatus(c.Status) === s).length;
		return acc;
	}, {});

	return (
		<div className="bg-white">
			<div className="max-w-6xl mx-auto px-6 py-10">
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
					<div>
						<h1 className="text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight">Campaigns</h1>
						<p className="text-base text-slate-600 mt-1">Design, send, and measure every email from one place.</p>
					</div>
					{(isLoading || usercampaigns.length > 0) && (
						<button
							type="button"
							onClick={() => setShowModal(true)}
							className="inline-flex items-center gap-2 self-start md:self-auto rounded-full px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-sm transition-colors">
							<Plus size={18} weight="bold"/>
							New campaign
						</button>
					)}
				</div>

				{isLoading ? (
					<TableSkeleton/>
				) : usercampaigns.length === 0 ? (
					<EmptyState onCreate={() => setShowModal(true)}/>
				) : (
					<>
						<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
							<div className="inline-flex flex-wrap gap-1 p-1 rounded-full bg-slate-50 border border-slate-100 self-start">
								{STATUS_FILTERS.map((s) => (
									<button
										key={s}
										type="button"
										onClick={() => setFilter(s)}
										className={
											'rounded-full px-4 py-1.5 text-sm font-medium transition-colors ' +
											(filter === s
												? 'bg-white text-slate-900 shadow-sm border border-slate-100'
												: 'text-slate-600 hover:text-slate-900')
										}>
										{s}
										<span className={'ml-2 text-xs ' + (filter === s ? 'text-emerald-600' : 'text-slate-400')}>
											{counts[s]}
										</span>
									</button>
								))}
							</div>
							<div className="relative md:w-72">
								<MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
								<input
									type="text"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder="Search campaigns…"
									className="w-full pl-9 pr-3 border border-slate-200 rounded-xl py-2.5 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition text-sm"/>
							</div>
						</div>

						<div className="rounded-2xl border border-slate-100 shadow-sm bg-white overflow-hidden">
							<div className="overflow-x-auto">
								<table className="w-full text-left">
									<thead className="bg-slate-50">
										<tr className="text-slate-600 text-xs uppercase tracking-wide font-semibold">
											<th className="py-3 px-4">Name</th>
											<th className="py-3 px-4">Status</th>
											<th className="py-3 px-4 text-right">Open rate</th>
											<th className="py-3 px-4 text-right">Click rate</th>
											<th className="py-3 px-4 text-right">Bounce</th>
											<th className="py-3 px-4">Last sent</th>
											<th className="py-3 px-4 text-right">Actions</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-slate-100">
										{filtered.length === 0 ? (
											<tr>
												<td colSpan={7} className="py-10 text-center text-slate-500 text-sm">
													No campaigns match this filter.
												</td>
											</tr>
										) : filtered.map((campaign) => {
											const status = displayStatus(campaign.Status);
											const sent = campaign.Sent;
											return (
												<tr
													key={campaign._id || campaign.campaignname}
													onClick={() => clickedCampaign(campaign)}
													className="hover:bg-emerald-50/40 transition cursor-pointer"
													title="Click to edit campaign">
													<td className="py-4 px-4">
														<div className="font-medium text-slate-900 truncate max-w-xs">{campaign.campaignname}</div>
														{campaign.targetLeads && campaign.targetLeads.length > 0 && (
															<div className="text-xs text-slate-500 mt-0.5">
																{campaign.targetLeads.length} {campaign.targetLeads.length === 1 ? 'lead' : 'leads'}
															</div>
														)}
													</td>
													<td className="py-4 px-4">
														<span className={'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ' + statusBadgeClasses(status)}>
															{status}
														</span>
													</td>
													<td className="py-4 px-4 text-right tabular-nums text-slate-700">{formatRate(campaign.Open, sent)}</td>
													<td className="py-4 px-4 text-right tabular-nums text-slate-700">{formatRate(campaign.Click, sent)}</td>
													<td className="py-4 px-4 text-right tabular-nums text-slate-700">{formatNumber(campaign.Bounce)}</td>
													<td className="py-4 px-4 text-slate-700">{formatLastSent(campaign)}</td>
													<td className="py-4 px-4 text-right">
														<button
															type="button"
															onClick={(e) => deleteCampaign(e, campaign)}
															title="Delete campaign"
															className="inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition">
															<Trash size={16} weight="bold"/>
														</button>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						</div>
					</>
				)}
			</div>

			<NewCampaignModal
				open={showModal}
				onClose={() => setShowModal(false)}
				onSubmit={handleCreate}
				submitting={submitting}/>
		</div>
	);
};

export default NewCampaign;
